import React, { useState, useEffect } from "react";
import {
  Form, Button, Modal, Row, Col, Table, Card, Spinner, Alert,
  Toast, ToastContainer, OverlayTrigger, Tooltip
} from "react-bootstrap";
import { PlusCircle, DashCircle, Truck, Eye, PencilSquare, Trash, Save, XCircle, Download } from "react-bootstrap-icons";
import "./deliveries.css";
import { Badge } from 'react-bootstrap';
import jsPDF from 'jspdf';
import 'jspdf-autotable';

const API_URL = "https://agpipefitting.org.et/api/deliveries";

const initialFormState = {
  date: "",
  to: "",
  dev_note: "",
  items: [{ description: "", unit: "pcs", qty: "", unit_price: "" }],
};

function FinishedGoodsCustomerDeliveryForm() {
  const [deliveries, setDeliveries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedDelivery, setSelectedDelivery] = useState(null);
  const [formData, setFormData] = useState(initialFormState);
  const [toast, setToast] = useState({ show: false, message: "", variant: "success" });

  const downloadPDF = (delivery) => {
    const doc = new jsPDF();
    const headerColor = [44, 62, 80];
    const textColor = [34, 34, 34];
    let startY = 60;

    doc.setFontSize(18);
    doc.setTextColor(...headerColor);
    doc.text(`Delivery Note - #${delivery.id}`, 14, 22);
    
    doc.setFontSize(12);
    doc.text("Company Name: AG Pipe Fitting Technology", 14, 32);

    const deliveryDate = new Date(delivery.date).toLocaleDateString();
    doc.text(`Delivery Date: ${deliveryDate}`, 14, 48);
    doc.text(`Recipient: ${delivery.to}`, 14, 54);

    if (delivery.dev_note) {
      doc.setFontSize(12);
      doc.text(`Delivery Note: ${delivery.dev_note}`, 14, 60);
      startY = 66;
    }

    const columns = [
      { header: 'Description', dataKey: 'description' },
      { header: 'Unit', dataKey: 'unit' },
      { header: 'Qty', dataKey: 'qty' },
      { header: 'Unit Price (ETB)', dataKey: 'unit_price' },
      { header: 'Total (ETB)', dataKey: 'total' }
    ];
    
    const rows = delivery.items.map(item => ({
      ...item,
      unit_price: Number(item.unit_price).toFixed(2),
      total: (item.qty * item.unit_price).toFixed(2)
    }));
    
    doc.autoTable({
      startY,
      head: [columns.map(c => c.header)],
      body: rows.map(row => columns.map(c => row[c.dataKey])),
      theme: 'grid',
      styles: { 
        fontSize: 10,
        cellPadding: 3,
        textColor: textColor
      },
      headerStyles: { 
        fillColor: headerColor,
        textColor: 255
      }
    });

    const totals = calculateTotals(delivery.items);
    const finalY = doc.lastAutoTable.finalY + 10;

    doc.setFontSize(12);
    doc.setTextColor(...textColor);
    doc.text(`Subtotal: ETB ${totals.subtotal.toFixed(2)}`, 14, finalY);
    doc.text(`VAT (15%): ETB ${totals.vat.toFixed(2)}`, 14, finalY + 6);
    
    doc.setFont('helvetica', 'bold');
    doc.text(`Total Amount: ETB ${totals.total.toFixed(2)}`, 14, finalY + 16);
    
    doc.setFont('helvetica', 'normal');
    const signatureY = finalY + 30;
    doc.setFontSize(12);
    doc.text("Prepared by: ___________________________", 14, signatureY);
    doc.text("Received by: ___________________________", 14, signatureY + 20);

    doc.save(`delivery-${delivery.id}.pdf`);
  };

  const fetchDeliveries = async () => {
    try {
      setLoading(true);
      const response = await fetch(API_URL);
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      
      const data = await response.json();
      if (!Array.isArray(data)) throw new Error("Invalid data format from API");

      const parsedData = data.map(delivery => ({
        ...delivery,
        date: new Date(delivery.date).toISOString().split('T')[0],
        dev_note: delivery.dev_note || "",
        items: delivery.items.map(item => ({
          ...item,
          qty: Number(item.qty) || 0,
          unit_price: Number(item.unit_price) || 0
        }))
      }));

      setDeliveries(parsedData);
      setError(null);
    } catch (err) {
      setError(err.message);
      showToast(`Failed to load deliveries: ${err.message}`, "danger");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchDeliveries(); }, []);

  const calculateTotals = (items) => {
    const subtotal = items.reduce((acc, item) => 
      acc + (Number(item.qty) || 0) * (Number(item.unit_price) || 0), 0);
    const vat = subtotal * 0.15;
    return { subtotal, vat, total: subtotal + vat };
  };

  const validateForm = () => {
    const errors = {};
    
    if (!formData.date) errors.date = "Date is required";
    if (!formData.to.trim()) errors.to = "Recipient is required";
    
    formData.items.forEach((item, index) => {
      if (!item.description.trim()) errors[`items[${index}].description`] = "Description required";
      if (!item.qty) errors[`items[${index}].qty`] = "Quantity required";
      if (!item.unit_price) errors[`items[${index}].unit_price`] = "Price required";
    });

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...formData.items];
    newItems[index][field] = value;
    setFormData({ ...formData, items: newItems });
  };

  const handleAddItem = () => {
    setFormData({
      ...formData,
      items: [...formData.items, { description: "", unit: "pcs", qty: "", unit_price: "" }]
    });
  };

  const handleRemoveItem = (index) => {
    if (formData.items.length > 1) {
      const newItems = formData.items.filter((_, i) => i !== index);
      setFormData({ ...formData, items: newItems });
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      const response = await fetch(API_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...formData,
          items: formData.items.map(item => ({
            ...item,
            qty: Number(item.qty),
            unit_price: Number(item.unit_price)
          }))
        })
      });

      if (!response.ok) throw new Error("Failed to create delivery");
      
      setShowCreateModal(false);
      setFormData(initialFormState);
      await fetchDeliveries();
      showToast("Delivery created successfully!", "success");
    } catch (err) {
      showToast(`Creation failed: ${err.message}`, "danger");
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      const response = await fetch(`${API_URL}/${selectedDelivery.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...formData,
          items: formData.items.map(item => ({
            ...item,
            qty: Number(item.qty),
            unit_price: Number(item.unit_price)
          }))
        })
      });

      if (!response.ok) throw new Error("Failed to update delivery");
      
      setShowEditModal(false);
      await fetchDeliveries();
      showToast("Delivery updated successfully!", "success");
    } catch (err) {
      showToast(`Update failed: ${err.message}`, "danger");
    }
  };

  const handleDelete = async () => {
    try {
      const response = await fetch(`${API_URL}/${selectedDelivery.id}`, {
        method: "DELETE"
      });

      if (!response.ok) throw new Error("Failed to delete delivery");
      
      setShowDeleteModal(false);
      await fetchDeliveries();
      showToast("Delivery deleted successfully!", "success");
    } catch (err) {
      showToast(`Deletion failed: ${err.message}`, "danger");
    }
  };

  const showToast = (message, variant = "success") => {
    setToast({ show: true, message, variant });
  };

  const openEditModal = (delivery) => {
    setFormData({
      date: delivery.date,
      to: delivery.to,
      dev_note: delivery.dev_note || "",
      items: delivery.items.map(item => ({
        ...item,
        qty: item.qty.toString(),
        unit_price: item.unit_price.toString()
      }))
    });
    setSelectedDelivery(delivery);
    setShowEditModal(true);
  };

  return (
    <div className="deliveries-container">
      <ToastContainer position="top-end" className="p-3">
        <Toast 
          show={toast.show} 
          onClose={() => setToast({ ...toast, show: false })}
          delay={5000} 
          autohide
          bg={toast.variant}
        >
          <Toast.Body className="text-white">{toast.message}</Toast.Body>
        </Toast>
      </ToastContainer>

      <header className="deliveries-header mb-5">
        <div className="d-flex justify-content-between align-items-center">
          <div>
            <h1 className="display-6 fw-bold text-primary">Delivery Management</h1>
            <p className="lead text-muted">Track and manage customer deliveries</p>
          </div>
          <Button 
            variant="primary" 
            onClick={() => setShowCreateModal(true)}
            className="rounded-pill px-4 py-2 d-flex align-items-center"
          >
            <Truck size={20} className="me-2" />
            New Delivery
          </Button>
        </div>
      </header>

      <Card className="shadow-lg">
        <Card.Body className="p-4">
          {error ? (
            <Alert variant="danger" className="text-center">
              <XCircle size={24} className="me-2" />
              {error}
            </Alert>
          ) : loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
              <p className="mt-3 text-muted">Loading deliveries...</p>
            </div>
          ) : deliveries.length === 0 ? (
            <div className="text-center py-5">
              <h5 className="text-muted mb-3">No deliveries found</h5>
              <Button variant="outline-primary" onClick={() => setShowCreateModal(true)}>
                Create New Delivery
              </Button>
            </div>
          ) : (
            <div className="table-responsive">
              <Table hover className="align-middle">
                <thead className="bg-light">
                  <tr>
                    <th>Date</th>
                    <th>Recipient</th>
                    <th>Items</th>
                    <th>Total Value</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {deliveries.map((delivery) => {
                    const totals = calculateTotals(delivery.items);
                    return (
                      <tr key={delivery.id}>
                        <td>{new Date(delivery.date).toLocaleDateString()}</td>
                        <td>{delivery.to}</td>
                        <td>
                          <Badge bg="primary" pill>
                            {delivery.items.length} items
                          </Badge>
                        </td>
                        <td className="fw-bold">ETB {totals.total.toFixed(2)}</td>
                        <td>
                          <Badge bg="success" pill>Completed</Badge>
                        </td>
                        <td>
                          <div className="d-flex gap-2">
                            <OverlayTrigger overlay={<Tooltip>View Details</Tooltip>}>
                              <Button
                                variant="outline-primary"
                                size="sm"
                                onClick={() => {
                                  setSelectedDelivery(delivery);
                                  setShowViewModal(true);
                                }}
                              >
                                <Eye size={16} />
                              </Button>
                            </OverlayTrigger>
                            <OverlayTrigger overlay={<Tooltip>Edit</Tooltip>}>
                              <Button
                                variant="outline-success"
                                size="sm"
                                onClick={() => openEditModal(delivery)}
                              >
                                <PencilSquare size={16} />
                              </Button>
                            </OverlayTrigger>
                            <OverlayTrigger overlay={<Tooltip>Delete</Tooltip>}>
                              <Button
                                variant="outline-danger"
                                size="sm"
                                onClick={() => {
                                  setSelectedDelivery(delivery);
                                  setShowDeleteModal(true);
                                }}
                              >
                                <Trash size={16} />
                              </Button>
                            </OverlayTrigger>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </div>
          )}
        </Card.Body>
      </Card>

      <CreateEditModal 
        show={showCreateModal}
        onHide={() => setShowCreateModal(false)}
        title="Create New Delivery"
        onSubmit={handleCreate}
        formData={formData}
        setFormData={setFormData}
        handleItemChange={handleItemChange}
        handleAddItem={handleAddItem}
        handleRemoveItem={handleRemoveItem}
        validationErrors={validationErrors}
        calculateTotals={calculateTotals}
      />

      <CreateEditModal 
        show={showEditModal}
        onHide={() => setShowEditModal(false)}
        title="Edit Delivery"
        onSubmit={handleUpdate}
        formData={formData}
        setFormData={setFormData}
        handleItemChange={handleItemChange}
        handleAddItem={handleAddItem}
        handleRemoveItem={handleRemoveItem}
        validationErrors={validationErrors}
        calculateTotals={calculateTotals}
      />

      <ViewModal 
        show={showViewModal}
        onHide={() => setShowViewModal(false)}
        delivery={selectedDelivery}
        calculateTotals={calculateTotals}
        downloadPDF={downloadPDF}
      />

      <DeleteConfirmationModal 
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        onConfirm={handleDelete}
        delivery={selectedDelivery}
      />
    </div>
  );
}

const CreateEditModal = ({ 
  show, onHide, title, onSubmit, formData, setFormData, 
  handleItemChange, handleAddItem, handleRemoveItem, 
  validationErrors, calculateTotals 
}) => (
  <Modal show={show} onHide={onHide} size="lg" centered>
    <Modal.Header closeButton className="bg-light">
      <Modal.Title>{title}</Modal.Title>
    </Modal.Header>
    <Form onSubmit={onSubmit}>
      <Modal.Body>
        <Row className="g-3 mb-4">
          <Col md={6}>
            <Form.Group>
              <Form.Label>Delivery Date</Form.Label>
              <Form.Control
                type="date"
                value={formData.date}
                onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                isInvalid={!!validationErrors.date}
              />
              <Form.Control.Feedback type="invalid">
                {validationErrors.date}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group>
              <Form.Label>Recipient</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter recipient name"
                value={formData.to}
                onChange={(e) => setFormData({ ...formData, to: e.target.value })}
                isInvalid={!!validationErrors.to}
              />
              <Form.Control.Feedback type="invalid">
                {validationErrors.to}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>
        </Row>

        <Row className="g-3 mb-4">
  <Col md={12}>
    <Form.Group>
      <Form.Label>DN/NT</Form.Label>
      <Form.Control
        type="text" 
        placeholder="DN/NT"
        value={formData.dev_note}
        onChange={(e) => setFormData({ ...formData, dev_note: e.target.value })}
      />
    </Form.Group>
  </Col>
</Row>


        <div className="mb-4">
          <h6 className="mb-3 text-primary">Items Details</h6>
          {formData.items.map((item, index) => (
            <Row key={index} className="g-2 mb-3 align-items-center">
              <Col md={5}>
                <Form.Control
                  placeholder="Item description"
                  value={item.description}
                  onChange={(e) => handleItemChange(index, "description", e.target.value)}
                  isInvalid={!!validationErrors[`items[${index}].description`]}
                />
                <Form.Control.Feedback type="invalid">
                  {validationErrors[`items[${index}].description`]}
                </Form.Control.Feedback>
              </Col>
              <Col md={2}>
                <Form.Select
                  value={item.unit}
                  onChange={(e) => handleItemChange(index, "unit", e.target.value)}
                >
                  <option value="pcs">Pieces</option>
                  <option value="kg">Kilograms</option>
                  <option value="m">Meters</option>
                  <option value="l">Liters</option>
                </Form.Select>
              </Col>
              <Col md={2}>
                <Form.Control
                  type="number"
                  min="1"
                  placeholder="Qty"
                  value={item.qty}
                  onChange={(e) => handleItemChange(index, "qty", e.target.value)}
                  isInvalid={!!validationErrors[`items[${index}].qty`]}
                />
                <Form.Control.Feedback type="invalid">
                  {validationErrors[`items[${index}].qty`]}
                </Form.Control.Feedback>
              </Col>
              <Col md={2}>
                <Form.Control
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder="Unit Price"
                  value={item.unit_price}
                  onChange={(e) => handleItemChange(index, "unit_price", e.target.value)}
                  isInvalid={!!validationErrors[`items[${index}].unit_price`]}
                />
                <Form.Control.Feedback type="invalid">
                  {validationErrors[`items[${index}].unit_price`]}
                </Form.Control.Feedback>
              </Col>
              <Col md={1} className="text-center">
                <Button
                  variant="link"
                  onClick={() => handleRemoveItem(index)}
                  disabled={formData.items.length <= 1}
                  className="text-danger"
                >
                  <DashCircle size={20} />
                </Button>
              </Col>
            </Row>
          ))}

          <Button 
            variant="link" 
            onClick={handleAddItem}
            className="text-primary p-0"
          >
            <PlusCircle size={20} className="me-2" />
            Add Item
          </Button>
        </div>

        <Card className="border-0 bg-light-subtle">
          <Card.Body>
            <Row className="g-0 fw-semibold">
              <Col md={8} className="text-end pe-3">
                <div className="mb-2">Subtotal:</div>
                <div className="mb-2">VAT (15%):</div>
                <div className="text-primary">Total Amount:</div>
              </Col>
              <Col md={4} className="text-start">
                <div className="mb-2">ETB {calculateTotals(formData.items).subtotal.toFixed(2)}</div>
                <div className="mb-2">ETB {calculateTotals(formData.items).vat.toFixed(2)}</div>
                <div className="text-primary">ETB {calculateTotals(formData.items).total.toFixed(2)}</div>
              </Col>
            </Row>
          </Card.Body>
        </Card>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Cancel
        </Button>
        <Button variant="primary" type="submit">
          <Save className="me-2" size={18} />
          {title.startsWith("Create") ? "Create Delivery" : "Save Changes"}
        </Button>
      </Modal.Footer>
    </Form>
  </Modal>
);

const ViewModal = ({ show, onHide, delivery, calculateTotals, downloadPDF }) => {
  if (!delivery) return null;
  const totals = calculateTotals(delivery.items);

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton className="bg-success">
        <Modal.Title>Delivery Details</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Row className="mb-4">
          <Col md={6}>
            <div className="text-muted small">Delivery Date</div>
            <div className="fw-semibold">
              {new Date(delivery.date).toLocaleDateString()}
            </div>
          </Col>
          <Col md={6}>
            <div className="text-muted small">Recipient</div>
            <div className="fw-semibold">{delivery.to}</div>
          </Col>
        </Row>

        {delivery.dev_note && (
          <Row className="mb-4">
            <Col md={12}>
              <div className="text-muted small">DN/NT</div>
              <div className="fw-semibold" style={{ whiteSpace: 'pre-wrap' }}>
                {delivery.dev_note}
              </div>
            </Col>
          </Row>
        )}

        <h6 className="mb-3 text-primary">Delivered Items</h6>
        <Table borderless className="mb-4">
          <thead className="bg-light">
            <tr>
              <th>Description</th>
              <th>Unit</th>
              <th>Qty</th>
              <th>Unit Price</th>
              <th>Total</th>
            </tr>
          </thead>
          <tbody>
            {delivery.items.map((item, index) => (
              <tr key={index}>
                <td>{item.description}</td>
                <td>{item.unit}</td>
                <td>{item.qty}</td>
                <td>ETB {item.unit_price.toFixed(2)}</td>
                <td>ETB {(item.qty * item.unit_price).toFixed(2)}</td>
              </tr>
            ))}
          </tbody>
        </Table>

        <Card className="border-0 bg-light-subtle">
          <Card.Body>
            <Row className="g-0 fw-semibold">
              <Col md={8} className="text-end pe-3">
                <div className="mb-2">Subtotal:</div>
                <div className="mb-2">VAT (15%):</div>
                <div className="text-primary">Total Amount:</div>
              </Col>
              <Col md={4} className="text-start">
                <div className="mb-2">ETB {totals.subtotal.toFixed(2)}</div>
                <div className="mb-2">ETB {totals.vat.toFixed(2)}</div>
                <div className="text-primary">ETB {totals.total.toFixed(2)}</div>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        <h6 className="mt-4 text-primary">Approval & Signatures</h6>
        <Table borderless className="mt-3">
          <tbody>
            <tr>
              <td><strong>ያዘጋጀዉ / Prepared by:</strong></td>
              <td>ስም: _____________________</td>
              <td>ፊርማ / Sig.: ___________</td>
            </tr>
            <tr>
              <td><strong>አስረካቢ / Supplied by:</strong></td>
              <td>ስም: _____________________</td>
              <td>ፊርማ / Sig.: ___________</td>
            </tr>
            <tr>
              <td><strong>የፈቀደዉ / Approved by:</strong></td>
              <td>ስም: _____________________</td>
              <td>ፊርማ / Sig.: ___________</td>
            </tr>
            <tr>
              <td><strong>ተረካቢ / Received by:</strong></td>
              <td>ስም: _____________________</td>
              <td>ፊርማ / Sig.: ___________</td>
            </tr>
          </tbody>
        </Table>

        <div className="mt-4 d-flex justify-content-end gap-2">
          <Button 
            variant="primary" 
            onClick={() => downloadPDF(delivery)}
            className="d-flex align-items-center"
          >
            <Download className="me-2" size={18} />
            Download PDF
          </Button>
          <Button variant="secondary" onClick={onHide}>
            Close
          </Button>
        </div>
      </Modal.Body>
    </Modal>
  );
};

const DeleteConfirmationModal = ({ show, onHide, onConfirm, delivery }) => (
  <Modal show={show} onHide={onHide} centered>
    <Modal.Header closeButton className="bg-light">
      <Modal.Title>Confirm Deletion</Modal.Title>
    </Modal.Header>
    <Modal.Body>
      {delivery && (
        <>
          <p className="lead">Are you sure you want to delete this delivery?</p>
          <div className="alert alert-danger">
            <strong>{delivery.to}</strong> - {new Date(delivery.date).toLocaleDateString()}
          </div>
        </>
      )}
    </Modal.Body>
    <Modal.Footer>
      <Button variant="secondary" onClick={onHide}>
        Cancel
      </Button>
      <Button variant="danger" onClick={onConfirm}>
        <Trash className="me-2" size={16} />
        Delete Permanently
      </Button>
    </Modal.Footer>
  </Modal>
);

export default FinishedGoodsCustomerDeliveryForm;